import { CdkContextMenuTrigger } from '@angular/cdk/menu';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EChartOption, SiChartComponent } from '@siemens/charts-ng';
import { SiCardModule } from '@siemens/element-ng/card';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiLoadingSpinnerModule } from '@siemens/element-ng/loading-spinner';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { SiResizeObserverDirective } from '@siemens/element-ng/resize-observer';

import { EventService } from '../../shared/services/event.service';
import { SettingsService } from '../../shared/services/settings.service';

type PlotType = 'speed_in_km/h' | 'SOE' | 'positionInm';

@Component({
  selector: 'app-plots',
  imports: [
    TranslateModule,
    SiCardModule,
    SiFormModule,
    FormsModule,
    SiLoadingSpinnerModule,
    SiChartComponent,
    SiMenuModule,
    CdkContextMenuTrigger,
    SiResizeObserverDirective
  ],
  templateUrl: './plots.component.html',
  styleUrl: './plots.component.scss'
})
export class PlotsComponent implements OnInit {
  eventService = inject(EventService);
  settingsService = inject(SettingsService);
  translateService = inject(TranslateService);
  readonly results = input.required<any>();

  readonly scheduleName = signal<string>('');

  /** Reactive theme signal - directly linked to settings service */
  readonly currentTheme = this.settingsService.theme;

  // Track which chart was right-clicked for context menu
  private activeChartType = '';
  private activeChartElement: HTMLElement | null = null;

  ngOnInit(): void {
    this.scheduleName.set(this.results().energy_consumption_results.timetable_name);
  }

  setActiveChart(event: MouseEvent, chartType: string): void {
    this.activeChartType = chartType;
    this.activeChartElement = (event.currentTarget as HTMLElement) ?? null;
  }

  downloadChartAsSVG(): void {
    const svgElement = this.activeChartElement?.querySelector('svg');

    if (!svgElement) return;

    // Clone the SVG to avoid modifying the original
    const clonedSvg = svgElement.cloneNode(true) as SVGElement;

    // Force all text elements to black for better visibility
    const textElements = clonedSvg.querySelectorAll('text, tspan');
    textElements.forEach(textEl => {
      (textEl as SVGTextElement).setAttribute('fill', '#000000');
      (textEl as SVGTextElement).style.fill = '#000000';
    });

    // Also handle axis lines and grid lines for better contrast
    const pathElements = clonedSvg.querySelectorAll('path, line');
    pathElements.forEach(pathEl => {
      const currentStroke = (pathEl as SVGElement).getAttribute('stroke');
      if (currentStroke && (currentStroke.includes('#fff') || currentStroke.includes('white'))) {
        (pathEl as SVGElement).setAttribute('stroke', '#333333');
      }
    });

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clonedSvg);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plot_${this.activeChartType || 'chart'}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  plotTypesMap: { value: PlotType; label: string }[] = [
    { value: 'speed_in_km/h', label: 'RESULTS.SPEED' },
    { value: 'SOE', label: 'RESULTS.SOE' },
    { value: 'positionInm', label: 'Position in m' }
  ];

  readonly selectedPlotType = signal<PlotType>('speed_in_km/h');
  readonly plotAxis = signal<'time' | 'position'>('time');

  // Single computed chart options based on selectedPlotType + plotAxis
  readonly chartOptions = computed<EChartOption | undefined>(() => {
    const plotType = this.selectedPlotType();
    const axis = this.plotAxis();
    const plotData = this.results().simulation_results;
    if (!plotData) return undefined;
    // Ensure theme changes trigger recomputation
    this.currentTheme();

    if (plotType === 'positionInm') {
      const xTicks = this.results().XTicks_plot_over_time;
      return this.buildPositionInTimeOption(plotData, xTicks ?? []);
    }

    if (plotType === 'speed_in_km/h') {
      if (axis === 'time') {
        const xTicks = this.results().XTicks_plot_over_time;
        return this.buildSpeedOverTimeOption(plotData, xTicks ?? []);
      }
      const xTicks = this.results().XTicks_plot_over_position;
      return this.buildSpeedOverPositionOption(plotData, xTicks ?? []);
    }

    if (plotType === 'SOE') {
      if (axis === 'time') {
        const xTicks = this.results().XTicks_plot_over_time;
        return this.buildSoeOverTimeOption(plotData, xTicks ?? []);
      }
      const xTicks = this.results().XTicks_plot_over_position;
      return this.buildSoeOverPositionOption(plotData, xTicks ?? []);
    }

    return undefined;
  });

  // Reusable station scatter series factory (time or position based)
  private createStationScatterSeries(ticks: any[], xAccessor: (t: any) => number) {
    return {
      type: 'scatter',
      xAxisIndex: 0,
      data: (ticks || []).map(t => [xAccessor(t), 0]),
      symbolSize: 9,
      symbol: 'triangle',
      silent: true,
      label: {
        show: true,
        position: 'bottom',
        formatter: (params: any) => ticks?.[params.dataIndex]?.station_name ?? '',
        rotate: 45,
        distance: 40,
        fontSize: 11,
        color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000'
      },
      z: 999
    } as any;
  }

  private getChartGridConfig() {
    return {
      containLabel: true,
      bottom: 100
    };
  }

  private getYAxisNameGap(position: 'left' | 'right') {
    return position === 'left' ? 34 : 30;
  }

  private getChartAnimationConfig() {
    return {
      animation: true,
      animationDuration: 700,
      animationEasing: 'cubicOut',
      animationDurationUpdate: 350,
      animationEasingUpdate: 'cubicOut',
      animationThreshold: 200000,
      progressive: 0,
      progressiveThreshold: 200000
    };
  }

  private buildPositionInTimeOption(plotData: any, xTicks: any[]): EChartOption {
    const concatenatedDrivingSegments = (plotData.driving_segments ?? []).map(
      (element: any) => element.data ?? []
    );
    const drivingSegments = [].concat(...concatenatedDrivingSegments);
    const concatenatedStationarySegments = (plotData.stationary_segments ?? []).map(
      (element: any) => element.data ?? []
    );
    const stationarySegments = [].concat(...concatenatedStationarySegments);

    const loadCycleData = [...drivingSegments, ...stationarySegments];
    loadCycleData.sort((a: any, b: any) => (a.time_in_s ?? 0) - (b.time_in_s ?? 0));

    const safeXTicks = Array.isArray(xTicks) ? xTicks : [];
    const filteredTicks = safeXTicks.filter((_: any, i: number) => i % 2 == 0);

    const seriesData = loadCycleData
      .map((d: any) => [d.time_in_s ?? 0, d.position_in_m ?? null])
      .filter((p: any) => p[1] !== null);

    const maxX = seriesData.length ? Math.max(...seriesData.map((p: any) => p[0])) : 0;
    const yLabel = 'Position in m';

    const option: EChartOption = {
      ...this.getChartAnimationConfig(),
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross', label: { show: false } } },
      grid: this.getChartGridConfig(),
      xAxis: [
        {
          type: 'value',
          min: 0,
          max: maxX,
          axisLabel: { show: false },
          axisPointer: { label: { show: false } },
          axisLine: {
            lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
          },
          splitLine: {
            lineStyle: {
              color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
            }
          }
        }
      ],
      yAxis: {
        type: 'value',
        name: yLabel,
        nameLocation: 'middle',
        nameGap: this.getYAxisNameGap('right'),
        position: 'right',
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
        },
        axisLabel: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' },
        splitLine: {
          lineStyle: {
            color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
          }
        }
      },
      dataZoom: [{ type: 'slider' }, { type: 'inside' }],
      series: [
        {
          type: 'line',
          data: seriesData,
          name: yLabel,
          symbol: 'none',
          areaStyle: { opacity: 0.15 }
        }
      ],
      backgroundColor: 'rgba(19, 24, 44, 0.03)',
      legend: [{ left: 10 }]
    } as EChartOption;

    const stationMarkLines = (filteredTicks || []).map((t: any) => ({
      xAxis: t.time_in_s ?? 0,
      xAxisIndex: 0,
      label: {
        formatter: t.time_in_clock_format ?? '',
        position: 'end',
        color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000',
        fontSize: 11
      },
      lineStyle: {
        color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        type: 'dashed'
      }
    }));

    if (option.series?.length) {
      (option.series[0] as any).markLine = {
        silent: true,
        symbol: ['none', 'none'],
        data: stationMarkLines
      };
    }

    option.series!.push({
      type: 'scatter',
      xAxisIndex: 0,
      data: (filteredTicks || []).map(t => [t.time_in_s ?? 0, t.position_in_m ?? 0]),
      symbolSize: 9,
      symbol: 'triangle',
      silent: true,
      label: {
        show: true,
        position: 'bottom',
        formatter: (params: any) => filteredTicks?.[params.dataIndex]?.station_name ?? '',
        rotate: 45,
        distance: 40,
        fontSize: 11,
        color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000'
      },
      z: 999
    } as any);
    return option;
  }

  private buildSpeedOverTimeOption(plotData: any, xTicks: any[]): EChartOption {
    const concatenatedDrivingSegments = (plotData.driving_segments ?? []).map(
      (element: any) => element.data ?? []
    );
    const drivingSegments = [].concat(...concatenatedDrivingSegments);
    const concatenatedStationarySegments = (plotData.stationary_segments ?? []).map(
      (element: any) => element.data ?? []
    );
    const stationarySegments = [].concat(...concatenatedStationarySegments);

    const loadCycleData = [...drivingSegments, ...stationarySegments];
    loadCycleData.sort((a: any, b: any) => (a.time_in_s ?? 0) - (b.time_in_s ?? 0));

    const safeXTicks = Array.isArray(xTicks) ? xTicks : [];
    const filteredTicks = safeXTicks.filter((_: any, i: number) => i % 2 == 0);

    const speedSeriesData = loadCycleData
      .map((d: any) => [d.time_in_s ?? 0, d['speed_in_km/h'] ?? null])
      .filter((p: any) => p[1] !== null);

    const maxX = speedSeriesData.length ? Math.max(...speedSeriesData.map((p: any) => p[0])) : 0;
    const yLabel = this.translateService.instant('RESULTS.PLOTS.SPEED_KMH');

    const option: EChartOption = {
      ...this.getChartAnimationConfig(),
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross', label: { show: false } } },
      grid: this.getChartGridConfig(),
      xAxis: [
        {
          type: 'value',
          min: 0,
          max: maxX,
          axisLabel: { show: false },
          axisPointer: { label: { show: false } },
          axisLine: {
            lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
          },
          splitLine: {
            lineStyle: {
              color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
            }
          }
        }
      ],
      yAxis: {
        type: 'value',
        name: yLabel,
        nameLocation: 'middle',
        nameGap: this.getYAxisNameGap('left'),
        position: 'left',
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
        },
        axisLabel: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' },
        splitLine: {
          lineStyle: {
            color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
          }
        }
      },
      dataZoom: [{ type: 'slider' }, { type: 'inside' }],
      series: [
        {
          type: 'line',
          data: speedSeriesData,
          name: yLabel,
          symbol: 'none',
          areaStyle: { color: 'rgba(0, 214, 214, 0.18)' }
        }
      ],
      backgroundColor: 'rgba(19, 24, 44, 0.03)',
      legend: [{ left: 10 }]
    } as EChartOption;

    if (plotData.maximum_speed_data?.length) {
      const speedLimitData = plotData.maximum_speed_data
        .map((d: any) => [d.time_in_s ?? 0, d.maximum_speed_data ?? null])
        .filter((p: any) => p[1] !== null);

      const speedLimitSeries = {
        type: 'line',
        data: speedLimitData,
        name: this.translateService.instant('RESULTS.PLOTS.MAX_SPEED'),
        symbol: 'none',
        step: 'hv',
        areaStyle: { color: 'rgba(0, 177, 64, 0.14)' }
      } as any;

      option.series!.push(speedLimitSeries);
    }

    const stationMarkLines = (filteredTicks || []).map((t: any) => ({
      xAxis: t.time_in_s ?? 0,
      label: {
        formatter: t.time_in_clock_format ?? '',
        position: 'end',
        color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000',
        fontSize: 11
      },
      lineStyle: {
        color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        type: 'dashed'
      }
    }));

    if (option.series?.length) {
      (option.series[0] as any).markLine = {
        silent: true,
        symbol: ['none', 'none'],
        data: stationMarkLines
      };
    }

    option.series!.push(
      this.createStationScatterSeries(filteredTicks, (t: any) => t.time_in_s ?? 0)
    );
    return option;
  }

  private buildSpeedOverPositionOption(plotData: any, xTicks: any[]): EChartOption {
    const concatenatedDrivingSegments = (plotData.driving_segments ?? []).map(
      (element: any) => element.data ?? []
    );
    const drivingSegments = [].concat(...concatenatedDrivingSegments);
    const concatenatedStationarySegments = (plotData.stationary_segments ?? []).map(
      (element: any) => element.data ?? []
    );
    const stationarySegments = [].concat(...concatenatedStationarySegments);

    let loadCycleData = [...drivingSegments, ...stationarySegments];

    const compareObjects = (a: any, b: any) => {
      if (a.position_in_m === b.position_in_m) {
        return a.time_in_s - b.time_in_s;
      }
      return a.position_in_m - b.position_in_m;
    };
    loadCycleData = loadCycleData.sort(compareObjects);
    const speedSeriesData = loadCycleData
      .map((d: any) => [(d.position_in_m ?? 0) / 1000, d['speed_in_km/h'] ?? null])
      .filter((p: any) => p[1] !== null);
    const maxX = speedSeriesData.length ? Math.max(...speedSeriesData.map((p: any) => p[0])) : 0;
    const yLabel = this.translateService.instant('RESULTS.PLOTS.SPEED_KMH');
    const option: EChartOption = {
      ...this.getChartAnimationConfig(),
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross', label: { show: false } } },
      grid: this.getChartGridConfig(),
      xAxis: [
        {
          type: 'value',
          min: 0,
          max: maxX,
          position: 'bottom',
          axisPointer: { label: { show: false } },
          axisLabel: {
            show: true,
            formatter: '{value} km',
            color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000',
            fontSize: 12,
            fontFamily: 'inherit'
          },
          axisLine: {
            lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
          },
          splitLine: {
            lineStyle: {
              color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
            }
          }
        }
      ],
      yAxis: {
        type: 'value',
        name: yLabel,
        nameLocation: 'middle',
        nameGap: this.getYAxisNameGap('left'),
        position: 'left',
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
        },
        axisLabel: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' },
        splitLine: {
          lineStyle: {
            color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
          }
        }
      },
      dataZoom: [{ type: 'slider' }, { type: 'inside' }],
      series: [
        {
          type: 'line',
          data: speedSeriesData,
          name: yLabel,
          symbol: 'none',
          areaStyle: { color: 'rgba(0, 214, 214, 0.18)' }
        }
      ],
      backgroundColor: 'rgba(19, 24, 44, 0.03)',
      legend: [{ left: 10 }]
    } as EChartOption;

    if (plotData.maximum_speed_data?.length) {
      const speedLimitData = plotData.maximum_speed_data
        .map((d: any) => [(d.position_in_m ?? 0) / 1000, d.maximum_speed_data ?? null])
        .filter((p: any) => p[1] !== null);

      const speedLimitSeries = {
        type: 'line',
        data: speedLimitData,
        name: this.translateService.instant('RESULTS.PLOTS.MAX_SPEED'),
        symbol: 'none',
        step: 'hv',
        areaStyle: { color: 'rgba(0, 177, 64, 0.14)' }
      } as any;
      option.series!.push(speedLimitSeries);
    }
    const stationMarkLines = (xTicks || []).map((t: any) => ({
      xAxis: (t.position_in_m ?? 0) / 1000,
      xAxisIndex: 0,
      label: { show: false },
      lineStyle: {
        color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        type: 'dashed'
      }
    }));

    if (option.series?.length) {
      (option.series[0] as any).markLine = {
        silent: true,
        symbol: ['none', 'none'],
        data: stationMarkLines
      };
    }

    option.series!.push(
      this.createStationScatterSeries(xTicks, (t: any) => (t.position_in_m ?? 0) / 1000)
    );
    return option;
  }

  private buildSoeOverTimeOption(plotData: any, xTicks: any[]): EChartOption {
    const concatenatedDrivingSegments = (plotData.driving_segments ?? []).map(
      (e: any) => e.data ?? []
    );
    const drivingSegments = [].concat(...concatenatedDrivingSegments);
    const concatenatedStationarySegments = (plotData.stationary_segments ?? []).map(
      (e: any) => e.data ?? []
    );
    const stationarySegments = [].concat(...concatenatedStationarySegments);
    const loadCycleData = [...drivingSegments, ...stationarySegments];
    loadCycleData.sort((a: any, b: any) => (a.time_in_s ?? 0) - (b.time_in_s ?? 0));

    const safeXTicks = Array.isArray(xTicks) ? xTicks : [];
    const filteredTicks = safeXTicks.filter((_: any, i: number) => i % 2 === 0);

    const seriesData = loadCycleData
      .map((d: any) => [d.time_in_s ?? 0, d.soe ?? null])
      .filter((p: any) => p[1] !== null);

    const maxX = seriesData.length ? Math.max(...seriesData.map((p: any) => p[0])) : 0;
    const yLabel = this.translateService.instant('RESULTS.PLOTS.USABLE_SOE');

    const option: EChartOption = {
      ...this.getChartAnimationConfig(),
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross', label: { show: false } } },
      grid: this.getChartGridConfig(),
      xAxis: [
        {
          type: 'value',
          min: 0,
          max: maxX,
          axisLabel: { show: false },
          axisPointer: { label: { show: false } },
          axisLine: {
            lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
          },
          splitLine: {
            lineStyle: {
              color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
            }
          }
        }
      ],
      yAxis: {
        type: 'value',
        name: yLabel,
        nameLocation: 'middle',
        nameGap: this.getYAxisNameGap('left'),
        position: 'left',
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
        },
        axisLabel: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' },
        splitLine: {
          lineStyle: {
            color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
          }
        }
      },
      dataZoom: [{ type: 'slider' }, { type: 'inside' }],
      series: [
        {
          type: 'line',
          data: seriesData,
          name: yLabel,
          symbol: 'none',
          areaStyle: { opacity: 0.15 }
        }
      ],
      backgroundColor: 'rgba(19, 24, 44, 0.03)',
      legend: [{ left: 10 }]
    } as EChartOption;

    const stationMarkLines = (filteredTicks || []).map((t: any) => ({
      xAxis: t.time_in_s ?? 0,
      xAxisIndex: 0,
      label: {
        formatter: t.time_in_clock_format ?? '',
        position: 'end',
        color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000',
        fontSize: 11
      },
      lineStyle: {
        color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        type: 'dashed'
      }
    }));
    if (option.series?.length) {
      (option.series[0] as any).markLine = {
        silent: true,
        symbol: ['none', 'none'],
        data: stationMarkLines
      };
    }

    option.series!.push(
      this.createStationScatterSeries(filteredTicks, (t: any) => t.time_in_s ?? 0)
    );
    return option;
  }

  private buildSoeOverPositionOption(plotData: any, xTicks: any[]): EChartOption {
    const concatenatedDrivingSegments = (plotData.driving_segments ?? []).map(
      (e: any) => e.data ?? []
    );
    const drivingSegments = [].concat(...concatenatedDrivingSegments);
    const concatenatedStationarySegments = (plotData.stationary_segments ?? []).map(
      (e: any) => e.data ?? []
    );
    const stationarySegments = [].concat(...concatenatedStationarySegments);
    let loadCycleData = [...drivingSegments, ...stationarySegments];

    const compareObjects = (a: any, b: any) => {
      if (a.position_in_m === b.position_in_m) return a.time_in_s - b.time_in_s;
      return a.position_in_m - b.position_in_m;
    };
    loadCycleData = loadCycleData.sort(compareObjects);

    const seriesData = loadCycleData
      .map((d: any) => [(d.position_in_m ?? 0) / 1000, d.soe ?? null])
      .filter((p: any) => p[1] !== null);

    const maxX = seriesData.length ? Math.max(...seriesData.map((p: any) => p[0])) : 0;
    const yLabel = this.translateService.instant('RESULTS.PLOTS.USABLE_SOE');
    const option: EChartOption = {
      ...this.getChartAnimationConfig(),
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross', label: { show: false } } },
      grid: this.getChartGridConfig(),
      xAxis: [
        {
          type: 'value',
          min: 0,
          max: maxX,
          position: 'bottom',
          axisPointer: { label: { show: false } },
          axisLabel: {
            show: true,
            formatter: '{value} km',
            color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000',
            fontSize: 12,
            fontFamily: 'inherit'
          },
          axisLine: {
            lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
          },
          splitLine: {
            lineStyle: {
              color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
            }
          }
        }
      ],
      yAxis: {
        type: 'value',
        name: yLabel,
        nameLocation: 'middle',
        nameGap: this.getYAxisNameGap('left'),
        position: 'left',
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
        },
        axisLabel: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' },
        splitLine: {
          lineStyle: {
            color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
          }
        }
      },
      dataZoom: [{ type: 'slider' }, { type: 'inside' }],
      series: [
        {
          type: 'line',
          data: seriesData,
          name: yLabel,
          symbol: 'none',
          areaStyle: { opacity: 0.15 }
        }
      ],
      backgroundColor: 'rgba(19, 24, 44, 0.03)',
      legend: [{ left: 10 }]
    } as EChartOption;
    const stationMarkLines = (xTicks || []).map((t: any) => ({
      xAxis: (t.position_in_m ?? 0) / 1000,
      xAxisIndex: 0,
      label: { show: false },
      lineStyle: {
        color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        type: 'dashed'
      }
    }));
    if (option.series?.length) {
      (option.series[0] as any).markLine = {
        silent: true,
        symbol: ['none', 'none'],
        data: stationMarkLines
      };
    }

    option.series!.push(
      this.createStationScatterSeries(xTicks, (t: any) => (t.position_in_m ?? 0) / 1000)
    );
    return option;
  }

  onChartResize(chart: any) {
    if (chart && typeof chart.resize === 'function') {
      chart.resize();
    }
  }
}
